import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ResearchPanel } from "@/components/ResearchPanel";
import { Users, Plus, Trash2, Crown, Wand2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

const LEADERSHIP_PROMPT =
  "I want to maintain calm, consistent leadership and keep a confident 'alpha-like' role above my dogs in the pack. Based on how my dogs are behaving, how do I establish and hold that leadership position, set boundaries and earn deference without conflict? What do wolf/canid pack researchers say about how the lead animal maintains its role, and how does that translate to me leading my dogs?";

export default function Behaviour() {
  const [dogs, setDogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [f, setF] = useState({ dog_id: "", behaviour: "", context: "", intensity: "medium", with_pack: false });
  const [seedQuery, setSeedQuery] = useState("");

  const loadLogs = useCallback(() => api.get("/behaviour").then((r) => setLogs(r.data)), []);
  useEffect(() => { api.get("/dogs").then((r) => setDogs(r.data)); loadLogs(); }, [loadLogs]);

  const dogName = (id) => dogs.find((d) => d.id === id)?.name || "Unknown";

  const add = async () => {
    if (!f.dog_id) { toast.error("Pick a dog"); return; }
    if (!f.behaviour.trim()) { toast.error("Describe the behaviour"); return; }
    await api.post("/behaviour", { ...f, date: today() });
    setF({ dog_id: "", behaviour: "", context: "", intensity: "medium", with_pack: false });
    toast.success("Behaviour logged"); loadLogs();
  };

  return (
    <div data-testid="behaviour-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-2xl bg-primary/15 flex items-center justify-center"><Users className="h-6 w-6 text-primary" /></div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Pack & Behaviour</h1>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">Monitor behaviour across your pack and research it against training science, dog forums and wolf/canid pack researchers.</p>

      {/* Leadership card */}
      <div className="rounded-2xl bg-gradient-to-br from-secondary/15 to-accent/10 border border-secondary/30 p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-secondary/20 flex items-center justify-center shrink-0"><Crown className="h-5 w-5 text-secondary" /></div>
          <div>
            <h3 className="font-head font-semibold">Your role as pack leader</h3>
            <p className="text-sm text-muted-foreground max-w-xl">Get AI guidance on establishing calm, consistent leadership over your dogs — informed by how lead animals hold their role in wolf/canid packs.</p>
          </div>
        </div>
        <Button data-testid="leadership-guidance" onClick={() => { setSeedQuery(LEADERSHIP_PROMPT); window.scrollTo({ top: 400, behavior: "smooth" }); }} className="rounded-full shrink-0">
          <Wand2 className="h-4 w-4 mr-1" /> Guide my leadership
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
          <h3 className="font-head font-semibold mb-3">Log a behaviour</h3>
          <div className="space-y-3">
            <div>
              <Label>Dog</Label>
              <select data-testid="beh-dog" value={f.dog_id} onChange={(e) => setF({ ...f, dog_id: e.target.value })} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select dog</option>
                {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <Textarea data-testid="beh-text" placeholder="Behaviour (e.g. guarding food from other dogs)" value={f.behaviour} onChange={(e) => setF({ ...f, behaviour: e.target.value })} />
            <Input data-testid="beh-context" placeholder="Context (when/where it happens)" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} />
            <div className="flex gap-2">
              <select data-testid="beh-intensity" value={f.intensity} onChange={(e) => setF({ ...f, intensity: e.target.value })} className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <Label className="cursor-pointer">Involves the whole pack</Label>
              <Switch data-testid="beh-pack" checked={f.with_pack} onCheckedChange={(v) => setF({ ...f, with_pack: v })} />
            </div>
            <Button data-testid="add-behaviour" onClick={add} className="w-full rounded-full"><Plus className="h-4 w-4 mr-1" /> Log behaviour</Button>
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl bg-card border border-border/70 p-5 shadow-sm">
          <h3 className="font-head font-semibold mb-3">Pack behaviour log</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto scroll-thin">
            {logs.map((l) => (
              <div key={l.id} className="rounded-xl border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{dogName(l.dog_id)}</span>
                  <div className="flex items-center gap-2">
                    {l.with_pack && <span className="text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-full">pack</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.intensity === "high" ? "bg-destructive/15 text-destructive" : l.intensity === "medium" ? "bg-secondary/15 text-secondary" : "bg-primary/15 text-primary"}`}>{l.intensity}</span>
                    <span className="text-xs text-muted-foreground">{fmt(l.date)}</span>
                    <button onClick={async () => { await api.delete(`/behaviour/${l.id}`); loadLogs(); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <p className="text-sm mt-1">{l.behaviour}</p>
                {l.context && <p className="text-xs text-muted-foreground mt-0.5">{l.context}</p>}
              </div>
            ))}
            {logs.length === 0 && <p className="text-sm text-muted-foreground">No behaviour logged yet.</p>}
          </div>
        </div>
      </div>

      <h2 className="font-head text-2xl font-bold tracking-tight mb-4">🐺 Research behaviour & pack dynamics</h2>
      <ResearchPanel
        key={seedQuery}
        kind="behaviour"
        dogs={dogs}
        defaultQuery={seedQuery}
        placeholder={{ label: "Describe the behaviour or pack situation", text: "e.g. My two males are posturing over sleeping spots and one guards doorways..." }}
        extraLabel="Researchers / sources you follow (optional)"
        extraPlaceholder="Name the wolf researchers or accounts you follow so the AI frames its answer around their work, e.g. 'the wolf researcher I follow on Instagram who lives with a wolf pack'..."
      />
    </div>
  );
}
